/* eslint-disable no-await-in-loop */
/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
import { useAuth0 } from '@auth0/auth0-react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, Button, Grid, IconButton, Snackbar, Stack } from '@mui/material'
import Alert from '@mui/material/Alert'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tooltip from '@mui/material/Tooltip'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import AppContext from '../../context/appContext'
import { useDataCollectionStatements } from '../../hooks/data/useDataCollectionStatements'
import { useSaveFinding } from '../../hooks/data/useFindings'
import { setDirtyForm } from '../../services/appPreferences/dirtyFormStorage'
import { ruutsApi } from '../../services/ruutsApi'
import { findingsTypes, formModes } from '../../utils/constants'
import { processLoading } from '../../utils/Loading/processLoading'
import { useUserRole } from '../../hooks/useUserRole'
import BaselineCorrectionConfirmModal from '../DataCollection/BaselineCorrectionConfirmModal/BaselineCorrectionConfirmModal'
import RegressionConfirmModal from '../DataCollection/RegressionConfirmModal/RegressionConfirmModal'
import FindingModal from '../Finding/FindingModal'
import { DynamicFormYearsContext } from './dynamicFormYearsContext'
import DynamicFieldSwitcher from './DynamicFieldSwitcher'
import { useDraftFormStorage } from './useDraftFormStorage'
import { checkDependencies, parseErrors } from './utils'

const closedRegressionModal = {
  open: false,
  impact: null,
  pendingPayload: null,
  requiresAdminConfirmation: true,
}

const closedBaselineCorrectionModal = { open: false, impact: null, pendingPayload: null }

const saveOutcomes = { saved: 'saved', awaitingConfirmation: 'awaitingConfirmation' }

const DataCollectionDynamicForm = ({
  selectedMetricEvent,
  dataCollectionStatementId,
  formMode,
  year,
  selectedPaddockIds,
  formDefinition,
  handleMetricReset,
  handleResetForm,
  handleDisableMap,
  allowMoreRecords,
  children,
}) => {
  const tableHeaderRef = useRef(null)
  const [observationDialogOpen, setObservationDialogOpen] = useState(false)
  const [regressionModal, setRegressionModal] = useState(closedRegressionModal)
  const [baselineCorrectionModal, setBaselineCorrectionModal] = useState(
    closedBaselineCorrectionModal,
  )

  const { setConfirmationModalConfig, userRoleSelected, setLoadingModalConfig, currentFarm } =
    useContext(AppContext)
  const { user } = useAuth0()
  const { isAdmin } = useUserRole()
  const [canUpdateIsMetricEventVerified, setCanUpdateIsMetricEventVerified] = useState(false)
  const [isMetricEventVerified, setIsMetricEventVerified] = useState(false)

  const { upsertDraft, removeFromDrafts } = useDraftFormStorage({
    namespace: formDefinition?.namespace,
    farmId: currentFarm.id,
    year,
  })

  const { data: dataCollectionStatements } = useDataCollectionStatements({
    farmId: currentFarm.id,
    userRole: userRoleSelected,
  })
  const saveFinding = useSaveFinding()

  const lastFarmSubdivisionYear = useMemo(() => {
    const realYears = (dataCollectionStatements ?? [])
      .map(item => item.FarmSubdivision?.year)
      .filter(y => Number.isInteger(Number.parseInt(y, 10)))
    return realYears.length ? Math.max(...realYears) : null
  }, [dataCollectionStatements])

  const getDefaultValues = () => {
    if (selectedMetricEvent !== null) {
      return selectedMetricEvent.data ? selectedMetricEvent.data : selectedMetricEvent
    }
    return formDefinition.fields.reduce(
      (acc, curr) => {
        if (curr.component_type === 'FieldArray') {
          acc[curr.name] = curr.defaultValues
          return acc
        }
        acc[curr.name] = curr.defaultValue
        return acc
      },
      { notApplicable: false },
    )
  }

  const {
    handleSubmit,
    formState: { errors, isDirty, touchedFields, dirtyFields },
    control,
    watch,
    reset,
    setValue,
    setError,
    clearErrors,
    getValues,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    defaultValues: getDefaultValues(),
  })

  const notApplicableValue = watch('notApplicable')

  const resetForm = useCallback(
    (values = null) => {
      reset(
        values ||
          formDefinition.fields.reduce(
            (acc, curr) => {
              if (curr.component_type === 'FieldArray') {
                acc[curr.name] = curr.defaultValues
                return acc
              }
              acc[curr.name] = curr.defaultValue
              return acc
            },
            {
              notApplicable: false,
            },
          ),
      )
      clearErrors()
      setIsMetricEventVerified(false)
      setDirtyForm(false)
    },
    [formDefinition.fields, reset, clearErrors, setIsMetricEventVerified],
  )

  const resetAndReloadForm = () => {
    resetForm()
    /* Set formSubmitCount * random number, to force metricEvents refresh on parent component. */
    // Refactor con un eventEmit / listener?
    handleMetricReset()
  }

  const handleTabChangeConfirm = () => {
    resetForm()
    handleResetForm()
  }

  const handleReset = async () => {
    if (isDirty) {
      setConfirmationModalConfig({
        open: true,
        title: 'Cambios sin guardar',
        message: 'Si cambia de pantalla, se perderán los cambios. ¿Desea continuar?',
        confirmLabel: 'CONTINUAR',
        cancelLabel: 'CANCELAR',
        confirmAction: () => handleTabChangeConfirm(),
      })
    } else {
      resetForm()
      handleResetForm()
    }
  }

  const buildSavePayload = ({ data, metricEventId }) => ({
    namespace: formDefinition.namespace,
    farmId: currentFarm.id,
    createdBy: user.name,
    metricEventId,
    formDefinitionVersion: formDefinition.version,
    data: {
      ...data,
      metricYear: year,
      selectedPaddocks: selectedPaddockIds,
      lastFarmSubdivisionYear,
    },
    dataCollectionStatementId,
    userRole: userRoleSelected,
  })

  const executeSave = async ({ payload }) => {
    await ruutsApi.saveMetrics(payload)
    removeFromDrafts(selectedMetricEvent?.id)
    resetAndReloadForm()
    setLoadingModalConfig({ open: false, successfulMessage: 'Datos guardados' })
  }

  // A confirmed re-send can be answered by the other confirmation (regression, then baseline
  // correction), so every save goes through here and the pending payload keeps the flags already added.
  const saveOrRequestConfirmation = async payload => {
    try {
      await executeSave({ payload })
      return saveOutcomes.saved
    } catch (saveError) {
      const response = saveError?.response
      if (response?.code === 409 && response.confirmationType === 'baselineCorrection') {
        setLoadingModalConfig({ open: false })
        setBaselineCorrectionModal({ open: true, impact: response.impact, pendingPayload: payload })
        return saveOutcomes.awaitingConfirmation
      }
      const requiresAdminConfirmation = response?.requiresAdminConfirmation
      if (response?.code === 409 && requiresAdminConfirmation !== undefined) {
        setLoadingModalConfig({ open: false })
        setRegressionModal({
          open: true,
          impact: response.impact,
          pendingPayload: payload,
          requiresAdminConfirmation,
        })
        return saveOutcomes.awaitingConfirmation
      }
      throw saveError
    }
  }

  // Nothing was saved while a confirmation modal is pending, so processLoading's success close is dropped.
  const saveWithLoading = async ({ loadingMessage, errorMessage, buildPayload }) => {
    let awaitingConfirmation = false
    await processLoading({
      setLoadingModalConfig: config => {
        if (!awaitingConfirmation) setLoadingModalConfig(config)
      },
      loadingMessage,
      successfulMessage: 'Datos guardados correctamente',
      errorMessage,
      doAction: async () => {
        const payload = buildPayload()
        if (!payload) return
        awaitingConfirmation =
          (await saveOrRequestConfirmation(payload)) === saveOutcomes.awaitingConfirmation
      },
    })
  }

  const resendWithConfirmation = async ({ pendingPayload, confirmationFlag, errorMessage }) => {
    await saveWithLoading({
      loadingMessage: 'Guardando métrica con confirmación...',
      errorMessage,
      buildPayload: () =>
        pendingPayload && {
          ...pendingPayload,
          data: { ...pendingPayload.data, [confirmationFlag]: true },
        },
    })
  }

  const handleRegressionConfirm = async () => {
    const { pendingPayload } = regressionModal
    setRegressionModal(closedRegressionModal)
    await resendWithConfirmation({
      pendingPayload,
      confirmationFlag: 'confirmedByAdmin',
      errorMessage: 'Error al confirmar la regresión, por favor intente nuevamente',
    })
  }

  const handleBaselineCorrectionConfirm = async () => {
    const { pendingPayload } = baselineCorrectionModal
    setBaselineCorrectionModal(closedBaselineCorrectionModal)
    await resendWithConfirmation({
      pendingPayload,
      confirmationFlag: 'confirmedBaselineCorrection',
      errorMessage:
        'Error al confirmar la corrección de línea de base, por favor intente nuevamente',
    })
  }

  const saveFormData = async data => {
    await saveWithLoading({
      loadingMessage: 'Guardando métrica...',
      errorMessage: 'Error al guardar la métrica, por favor intente nuevamente',
      buildPayload: () => {
        const metricEventId = !selectedMetricEvent?.isDraft ? selectedMetricEvent?.id : null
        return buildSavePayload({ data, metricEventId })
      },
    })
  }

  const saveDraft = () => {
    const draft = {
      ...getValues(),
      metricYear: year,
      selectedPaddocks: selectedPaddockIds,
      lastFarmSubdivisionYear,
    }
    draft.id = selectedMetricEvent?.id || null
    upsertDraft(draft)
    resetAndReloadForm()
  }

  const handleSaveVerificationFormData = async value => {
    try {
      setLoadingModalConfig({
        open: true,
        dialogMessage: 'Guardando verificación',
      })

      await ruutsApi.saveVerificationMetric({
        metricEventId: selectedMetricEvent?.id || null,
        isVerified: value,
        dataCollectionStatementId,
        userRole: userRoleSelected,
      })

      resetAndReloadForm()
      setLoadingModalConfig({
        open: false,
        successfulMessage: 'Datos guardados',
      })
    } catch (e) {
      setLoadingModalConfig({
        open: false,
        error: e,
        errorMessage: 'Error al guardar la métrica, por favor intente nuevamente',
      })
    }
  }

  const openObservationDialog = () => {
    setObservationDialogOpen(true)
  }

  const closeObservationDialog = () => {
    setObservationDialogOpen(false)
    resetAndReloadForm()
  }

  const checkDisableSubmit = useCallback(() => {
    const editingRecord = !!selectedMetricEvent
    const formChanged = Object.keys(touchedFields).length > 0 || Object.keys(dirtyFields).length > 0

    if (editingRecord) {
      return false
    }
    if (formChanged && allowMoreRecords) {
      return false
    }

    return true
  }, [selectedMetricEvent, dirtyFields, touchedFields, allowMoreRecords])

  const getSubmitTooltipMessage = useCallback(() => {
    if (!checkDisableSubmit()) return ''

    const formChanged = Object.keys(touchedFields).length > 0 || Object.keys(dirtyFields).length > 0

    if (
      formDefinition?.layout?.showMap &&
      (!selectedPaddockIds || selectedPaddockIds.length === 0) &&
      !notApplicableValue
    ) {
      return 'Debe seleccionar al menos un lote.'
    }

    if (!formChanged) {
      return 'Complete o modifique el formulario para guardar.'
    }

    return 'Faltan campos por completar o hay errores.'
  }, [
    checkDisableSubmit,
    formDefinition,
    selectedPaddockIds,
    notApplicableValue,
    touchedFields,
    dirtyFields,
  ])

  const checkDisableDraft = () => selectedMetricEvent && !selectedMetricEvent?.isDraft

  const handleSubmitFinding = async data => {
    try {
      setLoadingModalConfig({
        open: true,
        dialogMessage: 'Guardando observación',
      })

      await saveFinding.mutateAsync({
        payload: {
          namespace: formDefinition.namespace,
          farmId: currentFarm.id,
          createdBy: user.name,
          metricEventId: selectedMetricEvent?.id || null,
          formDefinitionVersion: formDefinition.version,
          type: data.type,
          metricEventsFieldsObserved: data.metricEventsFieldsObserved,
          comment: data.comment,
          dataCollectionStatementId,
          userRole: userRoleSelected,
        },
      })
      const successfulMessage = 'Observación guardada exitosamente'
      setLoadingModalConfig({
        open: false,
        successfulMessage,
      })
      closeObservationDialog()
    } catch (error) {
      setLoadingModalConfig({
        open: false,
        error,
        errorMessage: 'Error al guardar la observación, por favor intente nuevamente',
      })
    }
  }

  useEffect(() => {
    handleDisableMap(notApplicableValue)
  }, [notApplicableValue, handleDisableMap])

  /* When selecting, will set the data to the form */
  useEffect(() => {
    let newIsMetricEventVerified = false
    let hasUnresolvedNonComplaintFindings = false
    if (selectedMetricEvent !== null) {
      hasUnresolvedNonComplaintFindings = selectedMetricEvent.Findings?.some(
        finding =>
          !finding.resolved &&
          [findingsTypes.NonComplaintToFix, findingsTypes.NonComplaintSevere].includes(
            finding.type,
          ),
      )
      resetForm(selectedMetricEvent.data || selectedMetricEvent)
      newIsMetricEventVerified = selectedMetricEvent.isVerified
    } else {
      resetForm()
    }
    setIsMetricEventVerified(newIsMetricEventVerified)
    setCanUpdateIsMetricEventVerified(
      !hasUnresolvedNonComplaintFindings &&
        formMode === formModes.Review &&
        selectedMetricEvent !== null,
    )
  }, [formMode, selectedMetricEvent, resetForm, setIsMetricEventVerified])

  const dynamicFormYearsContextValue = useMemo(
    () => ({ year, lastFarmSubdivisionYear }),
    [year, lastFarmSubdivisionYear],
  )

  const isMetricEventVerifiedTooltip = () => {
    if (selectedMetricEvent === null) return ''
    if (!canUpdateIsMetricEventVerified)
      return 'No se puede verificar. Se necesita permisos de revisión y que la métrica no posea observaciones de tipo NO Conformidad sin resolver'
    return isMetricEventVerified ? 'No verificar' : 'Verificar'
  }

  const handleConfirmVerify = () => {
    setConfirmationModalConfig({
      open: true,
      title: 'Confirmar modificación sobre la métrica',
      message: isMetricEventVerified ? '¿Marcar como NO verificada?' : '¿Marcar como verificada?',
      confirmLabel: 'CONTINUAR',
      cancelLabel: 'CANCELAR',
      confirmAction: () => handleSaveVerificationFormData(!isMetricEventVerified),
    })
  }

  return (
    <>
      {formDefinition.fields && formDefinition.fields.length > 0 && (
        <FormProvider
          {...{
            watch,
            control,
            reset,
            setError,
            getValues,
            setValue,
            clearErrors,
          }}
        >
          <DynamicFormYearsContext.Provider value={dynamicFormYearsContextValue}>
            <Box
              component="form"
              pb={2}
              pl={2}
              pr={2}
              pt={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                height: '75vh',
                justifyContent: 'flex-start',
                overflowY: 'scroll',
                overflowX: 'hidden',
                scrollbarWidth: '10px',
              }}
              onSubmit={handleSubmit(saveFormData)}
            >
              <Stack
                alignItems="center"
                direction="row"
                display="flex"
                justifyContent="space-between"
              >
                {children}
                {!formDefinition.required &&
                  checkDependencies(
                    formDefinition?.dependencies?.requiredMessage?.display,
                    'display',
                    -1,
                    watch,
                  ) && (
                    <Box sx={{ padding: 0.5, backgroundColor: '#eceff1' }}>
                      <Controller
                        control={control}
                        defaultValue={false}
                        name="notApplicable"
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value}
                                disabled={formDefinition.requiredForm || formMode.readOnly}
                                onChange={(evt, value) => {
                                  field.onChange(value)
                                  setDirtyForm(true)
                                }}
                              />
                            }
                            label={
                              formDefinition.requiredMessage
                                ? formDefinition.requiredMessage
                                : 'No aplica'
                            }
                          />
                        )}
                      />
                    </Box>
                  )}
              </Stack>
              {!notApplicableValue && (
                <fieldset style={{ color: 'inherit' }}>
                  <Grid container spacing={2}>
                    {!!selectedMetricEvent && (
                      <Grid item xs={12}>
                        <Box>
                          <Chip
                            color="secondary"
                            label={`${formMode.label} #${selectedMetricEvent.dataIndex}`}
                            size="small"
                            variant="contained"
                          />
                        </Box>
                      </Grid>
                    )}
                    {formDefinition.fields.map((formField, index) => (
                      <DynamicFieldSwitcher
                        key={formField.name || `field-${index}`}
                        checkDependencies={checkDependencies}
                        control={control}
                        formField={formField}
                        readOnly={formMode.readOnly}
                      />
                    ))}
                  </Grid>
                </fieldset>
              )}
            </Box>
            <Stack
              backgroundColor="#E3E9EF"
              bottom={0}
              direction="column"
              display="flex"
              position="sticky"
              spacing={1}
              width="100vw"
              zIndex={1000}
            >
              {errors && Object.keys(errors).length > 0 && (
                <Snackbar key="error-snackbar" open={!!errors}>
                  <Alert
                    severity="error"
                    onClose={() => {
                      clearErrors()
                    }}
                  >
                    {parseErrors(errors)[0]}
                  </Alert>
                </Snackbar>
              )}
            </Stack>

            <Stack
              ref={tableHeaderRef}
              backgroundColor="#E3E9EF"
              bottom={0}
              direction="column"
              display="flex"
              id="anual-data-table-header"
              position="sticky"
              spacing={1}
              width="100vw"
              zIndex={900}
            >
              <Stack direction="row" display="flex" p={1} spacing={1}>
                {formMode === formModes.Review && (
                  <Tooltip title={isMetricEventVerifiedTooltip()}>
                    <Box>
                      <IconButton
                        color={isMetricEventVerified ? 'success' : 'error'}
                        disabled={!canUpdateIsMetricEventVerified}
                        onClick={handleConfirmVerify}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
                {!formMode.readOnly && (
                  <Tooltip placement="top" title={getSubmitTooltipMessage()}>
                    <span>
                      <Button
                        disabled={checkDisableSubmit()}
                        size="small"
                        variant="contained"
                        onClick={handleSubmit(saveFormData)}
                      >
                        GUARDAR
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {formMode === formModes.Review && !!selectedMetricEvent && (
                  <>
                    <Button
                      size="small"
                      sx={{ backgroundColor: '#D6B714', '&:hover': { backgroundColor: '#fbc02d' } }}
                      variant="contained"
                      onClick={openObservationDialog}
                    >
                      OBSERVAR
                    </Button>
                    <FindingModal
                      buttonValue="confirm"
                      cancelLabel="Cancelar"
                      formDefinition={formDefinition}
                      handleClose={closeObservationDialog}
                      handleConfirm={handleSubmitFinding}
                      open={observationDialogOpen}
                      saveLabel="Guardar"
                    />
                  </>
                )}
                {!formMode.readOnly && (
                  <Button
                    disabled={checkDisableDraft()}
                    size="small"
                    variant="outlined"
                    onClick={() => saveDraft()}
                  >
                    BORRADOR
                  </Button>
                )}
                <Button
                  color="error"
                  disabled={checkDisableSubmit()}
                  size="small"
                  variant="outlined"
                  onClick={handleReset}
                >
                  {formMode === formModes.Review ? 'CANCELAR' : 'LIMPIAR'}
                </Button>
              </Stack>
            </Stack>
          </DynamicFormYearsContext.Provider>
        </FormProvider>
      )}
      {regressionModal.open && regressionModal.impact && (
        <RegressionConfirmModal
          impact={regressionModal.impact}
          isAdmin={isAdmin}
          open={regressionModal.open}
          requiresAdminConfirmation={regressionModal.requiresAdminConfirmation}
          onCancel={() => setRegressionModal(closedRegressionModal)}
          onConfirm={handleRegressionConfirm}
        />
      )}
      {baselineCorrectionModal.open && baselineCorrectionModal.impact && (
        <BaselineCorrectionConfirmModal
          impact={baselineCorrectionModal.impact}
          isAdmin={isAdmin}
          open={baselineCorrectionModal.open}
          onCancel={() => setBaselineCorrectionModal(closedBaselineCorrectionModal)}
          onConfirm={handleBaselineCorrectionConfirm}
        />
      )}
    </>
  )
}

export default DataCollectionDynamicForm
